import $ from "jquery";
import EventEmitter from "eventemitter3";
import {gsap} from "gsap/all";
import decisionLogTemplate from '../../../../templates/ai-control/components/widgets-decision-log.twig.html';

export class DecisionLogs extends EventEmitter {

  constructor(target, algoData, equipmentData) {
    super();

    this.$widget = target;
    this.equipment = null; // will be set in parent
    this.algoData = algoData;
    this.equipmentData = equipmentData;

    this.preferredUnit = 'fahrenheit';
    this.currentBuildingType = null;
    this.currentKey = null;
    this.$logContainer = this.$widget.find('.decision-log-container');
    this.currentLog = null;

    this.maxLogsLinesBeforeScroll = 7;
    this.lineHeight = 32;

    this.timeline = null;

    this.ready = false;

    this.init();
  }

  init() {
    // If equipment widget exist, decision log is smaller
    if (this.$widget.prev('[data-widget-type="equipment"]').length) {
      this.maxLogsLinesBeforeScroll = 5;
    } else {
      this.maxLogsLinesBeforeScroll = 7;
    }

    this.$logContainer.css({
      height: `${this.maxLogsLinesBeforeScroll * this.lineHeight}px`,
    })
  }

  update(key, buildingType) {

    this.currentKey = key;
    this.currentBuildingType = buildingType;

    // Now let's see if we need to change the decision log and start the animation over
    const newLog = this.findDecisionLog(this.currentBuildingType, this.currentKey);

    if (newLog) {
      // Start over only if we actually switched to another log based on building and key
      // if (this.currentLog === null || this.currentLog.internal_label !== newLog.internal_label) {
      this.currentLog = newLog;

      // console.log("Decision log changed to", this.currentLog.internal_label, this.currentLog, this.$logContainer);
      this.changeLog();
      // }
    } else {
      // Used by Shoto - Base mode
      this.hideLogs();
    }

  }

  async changeLog() {

    if (this.equipment) {
      this.equipment.changeEquipements(this.currentBuildingType, this.currentLog.mode);
    }

    await this.hideLogs();
    const finalLogEntries = this.buildDynamicDecisionLogsEntries(this.currentBuildingType, this.currentLog.mode);
    this.initTimeline(finalLogEntries);
    gsap.set(this.$logContainer, {
      autoAlpha: 1,
    })

    this.startTimeline();
  }


  buildDynamicDecisionLogsEntries(buildingType, mode) {

    // Get building equipment
    const buildingEquipments = this.algoData.buildings[buildingType]?.equipments;

    // Simple regex that capture [AHU_XX] HEAT_STG_1
    const pattern = /\[[A-Za-z]+_XX+\]/g;

    // Create dynamic entries
    const finalLogEntries = [];
    for (const logEntry of this.currentLog.content) {
      const isDynamicEntry = logEntry.label.fr.includes("XX]") || logEntry.label.en.includes("XX]");
      if (isDynamicEntry) {
        // Build entry for each equipment
        const equipments = (Array.isArray(logEntry.equipment) ? logEntry.equipment : [logEntry.equipment]).filter((el) => buildingEquipments.includes(el));
        for (const [index, equipment] of equipments.entries()) {
          const isLast = (index === equipments.length - 1);
          const equipment_label = this.equipmentData[equipment].label;
          // Alter original log data
          finalLogEntries.push({
            ...logEntry,
            equipment: equipment,
            label: {
              fr: logEntry.label.fr.replace(pattern, equipment_label?.fr),
              en: logEntry.label.en.replace(pattern, equipment_label?.en)
            },
            value: this.applyUnitPreference({...logEntry.value}, this.preferredUnit),
            // Use duration_last_log value for last dynamic entry (using isLast)
            duration: (isLast && Number.isFinite(logEntry.duration_last_log)) ? logEntry.duration_last_log : logEntry.duration,
          });
        }
      } else {
        // Simply add entry without duplications, but still apply unit preference
        let equipments = Array.isArray(logEntry.equipment) ? logEntry.equipment : [logEntry.equipment];

        // Compass Chaos mode exception
        const isChaos = logEntry.equipment_value?.toString().includes("chaos")
        if (isChaos) equipments = [logEntry.equipment];

        for (const equipment of equipments) {
          finalLogEntries.push({...logEntry, equipment, value: this.applyUnitPreference({...logEntry.value}, this.preferredUnit)});
        }
      }
    }

    // Repeat content of decision logs, if loop_content is defined and set greater thatn 1
    if (this.currentLog.loop_content > 1) {
      const singleLoopContent = [...finalLogEntries]
      for (let i = 1; i < this.currentLog.loop_content; i++) {
        finalLogEntries.push(...singleLoopContent)
      }
    }

    // Render HTML and remplace DOM using nunjucks
    const renderedLogsHTML = decisionLogTemplate({decisionLogEntries: finalLogEntries});
    this.$logContainer.html(renderedLogsHTML);

    return finalLogEntries;
  }

  initTimeline(finalLogEntries) {
    this.stopLogs();

    if (this.currentLog) {
      // Let's build the timeline based on the decision logs content
      // We can set a delay so the timeline doesn't start immediately while the screen is fading in
      this.timeline = new gsap.timeline({paused: true});

      this.timeline.addLabel("start");
      this.timeline.set(this.$logContainer, {
        y: 0,
      });

      // Small delay before starting the logs display
      let currentPauseDuration = 1;

      for (const [index, logEntry] of finalLogEntries.entries()) {

        this.timeline.fromTo(this.$logContainer.find(".decision-log-element").eq(index), {
          y: 10,
          autoAlpha: 0,
        }, {
          y: 0,
          autoAlpha: 1,
          duration: app.settings.decision_logs_anim_duration
        }, `+=${currentPauseDuration}`);

        // save a label to define when the entry animation starts for easier queueing
        this.timeline.addLabel(`entry-${index}`, "<");

        // Let's trigger the equipment change immediately before the notification comes up so they are in sync
        if (logEntry.equipment !== null && this.equipment !== null) {
          this.timeline.add(() => {

            // Compass Chaos mode exception
            const isChaos = logEntry.equipment_value.toString().includes("chaos")
            if (isChaos) {
              for (let [i, equipment] of logEntry.equipment.entries()) {
                const isChaosOn = logEntry.equipment_value.toString() === "chaos_on";
                const equipment_value = isChaosOn ? ([0,4,5,6,7].includes(i)? "heating" : "cooling") : "active"
                this.equipment.updateEquipmentsState(equipment, equipment_value, true);
              }
              return;
            }

            this.equipment.updateEquipmentsState(logEntry.equipment, logEntry.equipment_value);
          }, `entry-${index}`); // start at same time as the entry
        }

        // If there is no more space to display new items, shift the container up
        if (index >= this.maxLogsLinesBeforeScroll) {
          // Handle the scrolling target position by taking into account the padding and border of each lines
          // let targetY = (((index + 1) - this.maxLogsLinesBeforeScroll) * currentLogLineHeight) - 2;
          let targetY = (((index + 1) - this.maxLogsLinesBeforeScroll) * this.lineHeight);

          // console.log(index, this.maxLogsLinesBeforeScroll, currentLogLineHeight, targetY)

          this.timeline.to(this.$logContainer, {
            y: `-${targetY}`,
            duration: app.settings.decision_logs_anim_duration,
          }, `entry-${index}`); // start at same time as the entry
        }

        // This will be used to offset the next entry so the duration is respected
        currentPauseDuration = app.settings.decision_logs_base_delay * logEntry.duration;
      }
    }

  }

  startTimeline() {
    if (this.timeline && this.ready) {
      this.timeline.play();
    }
  }

  findDecisionLog(building, key) {
    return this.algoData.logs.find(log => {
      const buildingMatch = log.buildings.includes(building);
      const keyMatch = log.keys.includes(key);

      return buildingMatch && keyMatch;
    });
  }

  start() {
    if (!this.ready) {
      this.ready = true;
      this.startTimeline();
    }
  }

  stopLogs() {
    app.killTimeline(this.timeline, this);
  }

  hideLogs() {
    this.stopLogs();
    return gsap.to(this.$widget.find('.decision-log-container'), {
      autoAlpha: 0,
    })
  }

  setPreferredUnit(preferredUnit) {
    this.preferredUnit = preferredUnit;
  }

  applyUnitPreference(textArray) {
    const pattern = /\[(\d+\.?\d*)°C\/(\d+\.?\d*)°F\]/;
    // const string = '[22.5°C/73.0°F]';

    for (let [textKey, textValue] of Object.entries(textArray)) {
      const matches = textValue.match(pattern);
      if (matches) {
        const celsius = matches[1];
        const fahrenheit = matches[2];
        const replaceValue = (this.preferredUnit === 'celsius') ? celsius + "°C" : fahrenheit + "°F";
        textArray[textKey] = textValue.replace(matches[0], replaceValue);
      }
    }

    return textArray;
  }

  reset() {
    this.hideLogs();

    this.currentBuildingType = null;
    this.currentKey = null;
    this.currentLog = null;

    if (this.equipment) {
      this.equipment.reset();
    }
  }
}
